import booleanContains from '@turf/boolean-contains'
import type { FeatureCollection, MultiPolygon, Point } from 'geojson'
import type { GeographyLayerData } from '~/models'

export type GeographyLayerFilterOption = {
  id: string
  label: string
  geometry: MultiPolygon
}

type FileGeographyLayer = FeatureCollection & {
  properties: {
    id: string
    name: string
  }
}

export function featureCollectionToMultiPolygon(
  collection: FeatureCollection
): MultiPolygon | null {
  const coordinates: MultiPolygon['coordinates'] = []

  for (const feature of collection.features) {
    if (feature.geometry?.type === 'Polygon') {
      coordinates.push(feature.geometry.coordinates)
    } else if (feature.geometry?.type === 'MultiPolygon') {
      coordinates.push(...feature.geometry.coordinates)
    }
  }

  return coordinates.length > 0 ? { type: 'MultiPolygon', coordinates } : null
}

export function geographyLayerFilterOptions(
  geographies: GeographyLayerData[],
  fileGeographies: FileGeographyLayer[]
): GeographyLayerFilterOption[] {
  const geographyOptions = geographies.flatMap((geography) => {
    const geometry = featureCollectionToMultiPolygon(geography.geojson)
    return geometry
      ? [{ id: geography.type, label: geography.label, geometry }]
      : []
  })

  const fileGeographyOptions = fileGeographies.flatMap((geography) => {
    const geometry = featureCollectionToMultiPolygon(geography)
    return geometry
      ? [
          {
            id: geography.properties.id,
            label: geography.properties.name,
            geometry,
          },
        ]
      : []
  })

  return [...geographyOptions, ...fileGeographyOptions]
}

export function filterListingsByGeometry<
  T extends { location?: { type: string; coordinates: number[] } },
>(listings: T[], geometry: MultiPolygon): T[] {
  return listings.filter(
    (listing) =>
      listing.location &&
      geometry.coordinates.some((coordinates) =>
        booleanContains(
          { type: 'Polygon', coordinates },
          listing.location as Point
        )
      )
  )
}
